// Block registry - Central configuration for all block types

// Existing block schemas
import { bannerSchema } from './banner/schema';
import { textImageSchema } from './text-image/schema';
import { cardGridSchema } from './card-grid/schema';
import { richTextSchema } from './rich-text/schema';

// New block schemas
import { videoEmbedSchema } from './video-embed/schema';
import { buttonCtaSchema } from './button-cta/schema';
import { faqSchema } from './faq/schema';
import { bulletinBoardSchema } from './bulletin-board/schema';
import { dividerSchema } from './divider/schema';
import { testimonialsSchema } from './testimonials/schema';
import { socialLinksSchema } from './social-links/schema';
import { youtubeGallerySchema } from './youtube-gallery/schema';
import { slidingTextSchema } from './sliding-text/schema';
import { coachingSchema } from './coaching/schema';
import { chessGameSchema } from './chess-game/schema';
import { dailyPuzzleSchema } from './daily-puzzle/schema';
import { calendlySchema } from './calendly/schema';
import { calComSchema } from './cal-com/schema';
import { calendarEmbedSchema } from './calendar-embed/schema';

// Chess-custom block schemas
import { blogSchema } from './blog/schema';
import { contactSchema } from './contact/schema';
import { donationsSchema } from './donations/schema';
import { referralsSchema } from './referrals/schema';
import { shopSchema } from './shop/schema';
import { singleProductSchema } from './single-product/schema';
import { featuredBlogSchema } from './featured-blog/schema';
import { downloadsSchema } from './downloads/schema';
import { mediaGallerySchema } from './media-gallery/schema';
import { fileEmbedSchema } from './file-embed/schema';
import { emailSubscribeSchema } from './email-subscribe/schema';
import { contentLibrarySchema } from './content-library/schema';
import { libraryShowcaseSchema } from './library-showcase/schema';
import { coachUpdatesSchema } from './coach-updates/schema';

// Existing editors and renderers
import BannerEditor, { getBannerExtraStyleControls } from './banner/BannerEditor';
import BannerRenderer from './banner/BannerRenderer';
import TextImageEditor, { getTextImageExtraStyleControls } from './text-image/TextImageEditor';
import TextImageRenderer from './text-image/TextImageRenderer';
import CardGridEditor, { getCardGridExtraStyleControls } from './card-grid/CardGridEditor';
import CardGridRenderer from './card-grid/CardGridRenderer';
import RichTextBlockEditor, { getRichTextExtraStyleControls } from './rich-text/RichTextBlockEditor';
import RichTextBlockRenderer from './rich-text/RichTextBlockRenderer';

// New editors and renderers
import VideoEmbedEditor from './video-embed/VideoEmbedEditor';
import VideoEmbedRenderer from './video-embed/VideoEmbedRenderer';
import ButtonCtaEditor from './button-cta/ButtonCtaEditor';
import ButtonCtaRenderer from './button-cta/ButtonCtaRenderer';
import FaqEditor from './faq/FaqEditor';
import FaqRenderer from './faq/FaqRenderer';
import BulletinBoardEditor from './bulletin-board/BulletinBoardEditor';
import BulletinBoardRenderer from './bulletin-board/BulletinBoardRenderer';
import DividerEditor, { getDividerExtraStyleControls } from './divider/DividerEditor';
import DividerRenderer from './divider/DividerRenderer';
import TestimonialsEditor from './testimonials/TestimonialsEditor';
import TestimonialsRenderer from './testimonials/TestimonialsRenderer';
import SocialLinksEditor, { getSocialLinksExtraStyleControls } from './social-links/SocialLinksEditor';
import SocialLinksRenderer from './social-links/SocialLinksRenderer';
import YouTubeGalleryEditor from './youtube-gallery/YouTubeGalleryEditor';
import YouTubeGalleryRenderer from './youtube-gallery/YouTubeGalleryRenderer';
import SlidingTextEditor, { getSlidingTextExtraStyleControls } from './sliding-text/SlidingTextEditor';
import SlidingTextRenderer from './sliding-text/SlidingTextRenderer';
import CoachingEditor from './coaching/CoachingEditor';
import CoachingRenderer from './coaching/CoachingRenderer';
import ChessGameEditor from './chess-game/ChessGameEditor';
import ChessGameRenderer from './chess-game/ChessGameRenderer';
import DailyPuzzleEditor from './daily-puzzle/DailyPuzzleEditor';
import DailyPuzzleRenderer from './daily-puzzle/DailyPuzzleRenderer';
import CalendlyEditor from './calendly/CalendlyEditor';
import CalendlyRenderer from './calendly/CalendlyRenderer';
import CalComEditor from './cal-com/CalComEditor';
import CalComRenderer from './cal-com/CalComRenderer';
import CalendarEmbedEditor from './calendar-embed/CalendarEmbedEditor';
import CalendarEmbedRenderer from './calendar-embed/CalendarEmbedRenderer';

// Chess-custom editors and renderers
import BlogEditor from './blog/BlogEditor';
import BlogRenderer from './blog/BlogRenderer';
import ContactEditor from './contact/ContactEditor';
import ContactRenderer from './contact/ContactRenderer';
import DonationsEditor from './donations/DonationsEditor';
import DonationsRenderer from './donations/DonationsRenderer';
import ReferralsEditor from './referrals/ReferralsEditor';
import ReferralsRenderer from './referrals/ReferralsRenderer';
import ShopEditor from './shop/ShopEditor';
import ShopRenderer from './shop/ShopRenderer';
import SingleProductEditor from './single-product/SingleProductEditor';
import SingleProductRenderer from './single-product/SingleProductRenderer';
import FeaturedBlogEditor from './featured-blog/FeaturedBlogEditor';
import FeaturedBlogRenderer from './featured-blog/FeaturedBlogRenderer';
import DownloadsEditor from './downloads/DownloadsEditor';
import DownloadsRenderer from './downloads/DownloadsRenderer';
import MediaGalleryEditor from './media-gallery/MediaGalleryEditor';
import MediaGalleryRenderer from './media-gallery/MediaGalleryRenderer';
import FileEmbedEditor from './file-embed/FileEmbedEditor';
import FileEmbedRenderer from './file-embed/FileEmbedRenderer';
import EmailSubscribeEditor from './email-subscribe/EmailSubscribeEditor';
import EmailSubscribeRenderer from './email-subscribe/EmailSubscribeRenderer';
import ContentLibraryEditor from './content-library/ContentLibraryEditor';
import ContentLibraryRenderer from './content-library/ContentLibraryRenderer';
import LibraryShowcaseEditor from './library-showcase/LibraryShowcaseEditor';
import LibraryShowcaseRenderer from './library-showcase/LibraryShowcaseRenderer';
import CoachUpdatesEditor from './coach-updates/CoachUpdatesEditor';
import CoachUpdatesRenderer from './coach-updates/CoachUpdatesRenderer';

// Favourite games block
import { favouriteGamesSchema } from './favourite-games/schema';
import FavouriteGamesEditor from './favourite-games/FavouriteGamesEditor';
import FavouriteGamesRenderer from './favourite-games/FavouriteGamesRenderer';

// Custom blocks (premium, per-profile activation)
import { initiativeTimelineSchema } from './initiative-timeline/schema';
import InitiativeTimelineEditor from './initiative-timeline/InitiativeTimelineEditor';
import InitiativeTimelineRenderer from './initiative-timeline/InitiativeTimelineRenderer';
import { endorsementWallSchema } from './endorsement-wall/schema';
import EndorsementWallEditor from './endorsement-wall/EndorsementWallEditor';
import EndorsementWallRenderer from './endorsement-wall/EndorsementWallRenderer';

/**
 * Block registry - defines all available block types
 * Add new block types here to make them available in the editor
 */
export const blockRegistry = {
  // ═══════════════════════════════════════════════════════════════
  // BUSINESS - Revenue & monetization
  // ═══════════════════════════════════════════════════════════════
  coaching: {
    ...coachingSchema,
    category: 'business',
    Editor: CoachingEditor,
    Renderer: CoachingRenderer,
  },
  'library-showcase': {
    ...libraryShowcaseSchema,
    category: 'business',
    Editor: LibraryShowcaseEditor,
    Renderer: LibraryShowcaseRenderer,
  },
  'coach-updates': {
    ...coachUpdatesSchema,
    category: 'business',
    Editor: CoachUpdatesEditor,
    Renderer: CoachUpdatesRenderer,
  },
  'content-library': {
    ...contentLibrarySchema,
    category: 'business',
    Editor: ContentLibraryEditor,
    Renderer: ContentLibraryRenderer,
  },
  shop: {
    ...shopSchema,
    category: 'business',
    Editor: ShopEditor,
    Renderer: ShopRenderer,
  },
  'single-product': {
    ...singleProductSchema,
    category: 'business',
    Editor: SingleProductEditor,
    Renderer: SingleProductRenderer,
  },
  donations: {
    ...donationsSchema,
    category: 'business',
    Editor: DonationsEditor,
    Renderer: DonationsRenderer,
  },
  events: {
    name: 'Events',
    label: 'Events',
    description: 'Display your chess tournaments and events',
    icon: 'Calendar',
    category: 'business',
    comingSoon: true,
    Editor: null,
    Renderer: null,
    defaultContent: {},
  },

  // ═══════════════════════════════════════════════════════════════
  // CONTENT - Text, blogs, and information
  // ═══════════════════════════════════════════════════════════════
  blog: {
    ...blogSchema,
    category: 'content',
    Editor: BlogEditor,
    Renderer: BlogRenderer,
  },
  'featured-blog': {
    ...featuredBlogSchema,
    category: 'content',
    Editor: FeaturedBlogEditor,
    Renderer: FeaturedBlogRenderer,
  },
  'rich-text': {
    ...richTextSchema,
    category: 'content',
    Editor: RichTextBlockEditor,
    Renderer: RichTextBlockRenderer,
    getExtraStyleControls: getRichTextExtraStyleControls,
  },
  'text-image': {
    ...textImageSchema,
    category: 'content',
    Editor: TextImageEditor,
    Renderer: TextImageRenderer,
    getExtraStyleControls: getTextImageExtraStyleControls,
  },
  'card-grid': {
    ...cardGridSchema,
    category: 'content',
    Editor: CardGridEditor,
    Renderer: CardGridRenderer,
    getExtraStyleControls: getCardGridExtraStyleControls,
  },
  faq: {
    ...faqSchema,
    category: 'content',
    Editor: FaqEditor,
    Renderer: FaqRenderer,
  },
  'bulletin-board': {
    ...bulletinBoardSchema,
    category: 'content',
    Editor: BulletinBoardEditor,
    Renderer: BulletinBoardRenderer,
  },

  // ═══════════════════════════════════════════════════════════════
  // MEDIA - Video and visual content
  // ═══════════════════════════════════════════════════════════════
  banner: {
    ...bannerSchema,
    name: 'Hero',
    category: 'media',
    Editor: BannerEditor,
    Renderer: BannerRenderer,
    getExtraStyleControls: getBannerExtraStyleControls,
  },
  'video-embed': {
    ...videoEmbedSchema,
    category: 'media',
    Editor: VideoEmbedEditor,
    Renderer: VideoEmbedRenderer,
  },
  'youtube-gallery': {
    ...youtubeGallerySchema,
    category: 'media',
    Editor: YouTubeGalleryEditor,
    Renderer: YouTubeGalleryRenderer,
  },
  'media-gallery': {
    ...mediaGallerySchema,
    category: 'media',
    Editor: MediaGalleryEditor,
    Renderer: MediaGalleryRenderer,
  },
  'file-embed': {
    ...fileEmbedSchema,
    category: 'media',
    Editor: FileEmbedEditor,
    Renderer: FileEmbedRenderer,
  },

  // ═══════════════════════════════════════════════════════════════
  // ENGAGEMENT - Conversion, interaction, and social proof
  // ═══════════════════════════════════════════════════════════════
  'button-cta': {
    ...buttonCtaSchema,
    category: 'engagement',
    Editor: ButtonCtaEditor,
    Renderer: ButtonCtaRenderer,
  },
  calendly: {
    ...calendlySchema,
    category: 'engagement',
    Editor: CalendlyEditor,
    Renderer: CalendlyRenderer,
  },
  'cal-com': {
    ...calComSchema,
    category: 'engagement',
    Editor: CalComEditor,
    Renderer: CalComRenderer,
  },
  'calendar-embed': {
    ...calendarEmbedSchema,
    category: 'engagement',
    Editor: CalendarEmbedEditor,
    Renderer: CalendarEmbedRenderer,
  },
  testimonials: {
    ...testimonialsSchema,
    category: 'engagement',
    Editor: TestimonialsEditor,
    Renderer: TestimonialsRenderer,
  },
  contact: {
    ...contactSchema,
    category: 'engagement',
    Editor: ContactEditor,
    Renderer: ContactRenderer,
  },
  'email-subscribe': {
    ...emailSubscribeSchema,
    category: 'engagement',
    Editor: EmailSubscribeEditor,
    Renderer: EmailSubscribeRenderer,
  },
  referrals: {
    ...referralsSchema,
    category: 'engagement',
    Editor: ReferralsEditor,
    Renderer: ReferralsRenderer,
  },
  'social-links': {
    ...socialLinksSchema,
    category: 'engagement',
    Editor: SocialLinksEditor,
    Renderer: SocialLinksRenderer,
    getExtraStyleControls: getSocialLinksExtraStyleControls,
  },
  downloads: {
    ...downloadsSchema,
    category: 'engagement',
    Editor: DownloadsEditor,
    Renderer: DownloadsRenderer,
  },

  // ═══════════════════════════════════════════════════════════════
  // CHESS - Chess-specific blocks
  // ═══════════════════════════════════════════════════════════════
  'chess-game': {
    ...chessGameSchema,
    category: 'chess',
    Editor: ChessGameEditor,
    Renderer: ChessGameRenderer,
  },
  'daily-puzzle': {
    ...dailyPuzzleSchema,
    category: 'chess',
    Editor: DailyPuzzleEditor,
    Renderer: DailyPuzzleRenderer,
  },
  'favourite-games': {
    ...favouriteGamesSchema,
    category: 'chess',
    Editor: FavouriteGamesEditor,
    Renderer: FavouriteGamesRenderer,
  },

  // ═══════════════════════════════════════════════════════════════
  // UTILITY - Structural and decorative elements
  // ═══════════════════════════════════════════════════════════════
  divider: {
    ...dividerSchema,
    category: 'utility',
    Editor: DividerEditor,
    Renderer: DividerRenderer,
    getExtraStyleControls: getDividerExtraStyleControls,
  },
  'sliding-text': {
    ...slidingTextSchema,
    category: 'utility',
    Editor: SlidingTextEditor,
    Renderer: SlidingTextRenderer,
    getExtraStyleControls: getSlidingTextExtraStyleControls,
  },

  // ═══════════════════════════════════════════════════════════════
  // CUSTOM - Premium per-profile components
  // ═══════════════════════════════════════════════════════════════
  'initiative-timeline': {
    ...initiativeTimelineSchema,
    category: 'custom',
    isCustom: true,
    Editor: InitiativeTimelineEditor,
    Renderer: InitiativeTimelineRenderer,
  },
  'endorsement-wall': {
    ...endorsementWallSchema,
    category: 'custom',
    isCustom: true,
    Editor: EndorsementWallEditor,
    Renderer: EndorsementWallRenderer,
  },
};

/**
 * Block categories for organizing in UI
 */
export const blockCategories = {
  business: {
    label: 'Business',
    description: 'Revenue and monetization',
  },
  content: {
    label: 'Content',
    description: 'Text, blogs, and information',
  },
  media: {
    label: 'Media',
    description: 'Video and visual content',
  },
  engagement: {
    label: 'Engagement',
    description: 'Conversion, interaction, and social proof',
  },
  chess: {
    label: 'Chess',
    description: 'Chess-specific blocks',
  },
  utility: {
    label: 'Utility',
    description: 'Structural and decorative elements',
  },
  custom: {
    label: 'Custom',
    description: 'Premium custom components',
  },
};

/**
 * Get block types as array for rendering in UI
 */
export const getBlockTypes = () => {
  return Object.entries(blockRegistry).map(([type, config]) => ({
    type,
    label: config.label,
    description: config.description,
    icon: config.icon,
    category: config.category,
  }));
};

/**
 * Get block types grouped by category
 */
export const getBlockTypesByCategory = () => {
  const grouped = {};

  Object.entries(blockRegistry).forEach(([type, config]) => {
    const category = config.category || 'other';
    if (!grouped[category]) {
      grouped[category] = [];
    }
    grouped[category].push({
      type,
      label: config.label,
      description: config.description,
      icon: config.icon,
      comingSoon: config.comingSoon || false,
    });
  });

  return grouped;
};

/**
 * Get editor component for a block type
 */
export const getBlockEditor = (type) => {
  const config = blockRegistry[type];
  return config ? config.Editor : null;
};

/**
 * Get renderer component for a block type
 */
export const getBlockRenderer = (type) => {
  const config = blockRegistry[type];
  return config ? config.Renderer : null;
};

/**
 * Get default content for a block type
 */
export const getBlockDefaultContent = (type) => {
  const config = blockRegistry[type];
  return config ? { ...config.defaultContent } : {};
};

/**
 * Get block schema
 */
export const getBlockSchema = (type) => {
  return blockRegistry[type] || null;
};
